import React from 'react'
import { formatDuration } from 'date-fns'
import { Loading, Text } from '@workwhile/ui'
import {
  BriefcaseBusiness,
  Calendar,
  Group,
  Hash,
  Percent,
  ReceiptText,
  SquareUser,
} from 'lucide-react'
import {
  CommandPaletteOption,
  CompanyEntry,
  CopyableValue,
  DetailsDescription,
  DetailsTitle,
  formatDate,
  getValue,
  SectionTitleWithButton,
} from '../CommandPalette_Utils'
import { useCompanyDetailsByIdQuery } from '../../../queries/company/useCompanyDetailsByIdQuery'
import { InfoTable } from '../../../components/InfoTable/InfoTable'
import { formatPercentage } from '../../../lib/percentage'

type CompanyDetailsProps = {
  company: CompanyEntry
  companyId?: number
  onNavigateToCompany: (companyId: string | number) => void
  onItemInteraction: (item: CommandPaletteOption) => void
}

export function CompanyDetails({
  company,
  companyId,
  onNavigateToCompany,
  onItemInteraction,
}: CompanyDetailsProps) {
  const handleSearch = () => {
    const id = company.id || company.name
    if (id && (typeof id === 'string' || typeof id === 'number')) {
      onItemInteraction(company)
      onNavigateToCompany(id)
    }
  }
  const currentCompanyId = companyId || Number(company.id)
  const { data: companyDetail, isLoading: isLoadingCompanyDetail } =
    useCompanyDetailsByIdQuery(currentCompanyId, 1000 * 60 * 5)

  // use companyDetail if it matches the current company to prevent stale data
  const validCompanyDetail =
    companyDetail && companyDetail.id === currentCompanyId
      ? companyDetail
      : undefined

  const formattedPaymentMethod = validCompanyDetail?.paymentMethod
    ? validCompanyDetail.paymentMethod === 'invoice'
      ? 'Invoice'
      : validCompanyDetail.paymentMethod === 'card'
        ? 'Card'
        : validCompanyDetail.paymentMethod === 'bank_account'
          ? 'Bank'
          : getValue(validCompanyDetail.paymentMethod)
    : 'Not set'

  const isOnInvoice = validCompanyDetail?.paymentMethod === 'invoice'
  const companyType = getValue(validCompanyDetail?.type)

  return (
    <>
      <DetailsTitle>
        {company.name}
        {isLoadingCompanyDetail ? (
          <span style={{ marginLeft: '8px' }}>
            <Loading type="button" />
          </span>
        ) : null}
      </DetailsTitle>
      <DetailsDescription>
        <div style={{ marginBottom: '16px' }}>
          <SectionTitleWithButton
            title="General Details"
            buttonText="Go To Company"
            onButtonClick={handleSearch}
            buttonTitle="Search for this company"
          />
          <InfoTable>
            <InfoTable.Row
              icon={<Hash size={14} />}
              label="Company ID"
              value={
                <CopyableValue
                  value={company.id}
                  fieldName="Company ID"
                  item={company}
                  onItemInteraction={onItemInteraction}
                />
              }
            />
            <InfoTable.Row
              icon={<Calendar size={14} />}
              label="Created"
              value={
                validCompanyDetail?.createdAt
                  ? formatDate(String(validCompanyDetail.createdAt))
                  : 'N/A'
              }
            />
            <InfoTable.Row
              icon={<BriefcaseBusiness size={14} />}
              label="Type"
              value={companyType}
            />
            <InfoTable.Row
              icon={<SquareUser size={14} />}
              label="Account Owner Name"
              value={
                <CopyableValue
                  value={validCompanyDetail?.accountOwner?.name ?? 'N/A'}
                  fieldName="Account Owner Name"
                  item={company}
                  onItemInteraction={onItemInteraction}
                />
              }
            />
            <InfoTable.Row
              icon={<SquareUser size={14} />}
              label="Account Owner ID"
              value={
                <CopyableValue
                  value={validCompanyDetail?.accountOwner?.id ?? 'N/A'}
                  fieldName="Account Owner ID"
                  item={company}
                  onItemInteraction={onItemInteraction}
                />
              }
            />
            <InfoTable.Row
              icon={<SquareUser size={14} />}
              label="Sales Owner Name"
              value={
                <CopyableValue
                  value={validCompanyDetail?.salesOwner?.name ?? 'N/A'}
                  fieldName="Sales Owner Name"
                  item={company}
                  onItemInteraction={onItemInteraction}
                />
              }
            />
            <InfoTable.Row
              icon={<SquareUser size={14} />}
              label="Sales Owner ID"
              value={
                <CopyableValue
                  value={validCompanyDetail?.salesOwner?.id ?? 'N/A'}
                  fieldName="Sales Owner ID"
                  item={company}
                  onItemInteraction={onItemInteraction}
                />
              }
            />
            <InfoTable.Row
              icon={<Group size={14} />}
              label="Company Group Name"
              value={
                <CopyableValue
                  value={validCompanyDetail?.companyGroup?.name ?? 'N/A'}
                  fieldName="Company Group Name"
                  item={company}
                  onItemInteraction={onItemInteraction}
                />
              }
            />
            <InfoTable.Row
              icon={<Group size={14} />}
              label="Company Group ID"
              value={
                <CopyableValue
                  value={validCompanyDetail?.companyGroup?.id ?? 'N/A'}
                  fieldName="Company Group ID"
                  item={company}
                  onItemInteraction={onItemInteraction}
                />
              }
            />
          </InfoTable>
        </div>

        <div>
          <Text
            fontSize={2}
            fontWeight={500}
            color="text"
            style={{ marginBottom: '8px' }}
          >
            Money
          </Text>
          <InfoTable>
            <InfoTable.Row
              icon={<ReceiptText size={14} />}
              label="Payment method"
              value={formattedPaymentMethod}
            />
            {isOnInvoice && validCompanyDetail?.invoiceDueDays ? (
              <InfoTable.Row
                icon={<Calendar size={14} />}
                label="Invoice terms"
                value={formatDuration({
                  days: validCompanyDetail.invoiceDueDays,
                })}
              />
            ) : null}
            {companyType === '1099' ? (
              <InfoTable.Row
                icon={<Percent size={14} />}
                label="Take rate"
                value={
                  validCompanyDetail?.takeRate1099
                    ? formatPercentage(validCompanyDetail.takeRate1099)
                    : 'N/A'
                }
              />
            ) : null}
            <InfoTable.Row
              icon={<Percent size={14} />}
              label="Tip take rate"
              value={
                validCompanyDetail?.tipTakeRate
                  ? formatPercentage(validCompanyDetail.tipTakeRate)
                  : 'N/A'
              }
            />
            <InfoTable.Row
              icon={<Percent size={14} />}
              label="Bonus take rate"
              value={
                validCompanyDetail?.bonusTakeRate
                  ? formatPercentage(validCompanyDetail.bonusTakeRate)
                  : 'N/A'
              }
            />
            {companyType === 'w2' ? (
              <>
                <InfoTable.Row
                  icon={<Percent size={14} />}
                  label="W2 Regular take rate"
                  value={
                    validCompanyDetail?.w2RegularTakeRate
                      ? formatPercentage(validCompanyDetail.w2RegularTakeRate)
                      : 'N/A'
                  }
                />
                <InfoTable.Row
                  icon={<Percent size={14} />}
                  label="W2 Double take rate"
                  value={
                    validCompanyDetail?.w2DoubleTakeRate
                      ? formatPercentage(validCompanyDetail.w2DoubleTakeRate)
                      : 'N/A'
                  }
                />
                <InfoTable.Row
                  icon={<Percent size={14} />}
                  label="W2 Overtime take rate"
                  value={
                    validCompanyDetail?.w2OvertimeTakeRate
                      ? formatPercentage(validCompanyDetail.w2OvertimeTakeRate)
                      : 'N/A'
                  }
                />
              </>
            ) : null}
          </InfoTable>
        </div>
      </DetailsDescription>
    </>
  )
}
